import React, { useEffect, useState, useCallback } from 'react';
import {
  CreditCard,
  DollarSign,
  Users,
  Building2,
  ShieldCheck,
  FileText,
  RefreshCw,
  Sliders,
  Download,
  Search,
  Receipt,
  Tag,
  History,
  Sparkles,
  Edit3,
  Info,
  CheckCircle2,
  AlertTriangle,
  Lock,
  Layers
} from 'lucide-react';
import { api, DEMO } from '../lib/api';
import Modal from './Modal';

const centsToDollars = cents => {
  const val = Number(cents) || 0;
  return '$' + (val / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = val => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(val);
  }
};

const formatDateTime = val => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(val);
  }
};

export default function BillingManagement({ user, notify, navigate }) {
  const [subTab, setSubTab] = useState('overview'); // overview | controls | ledger | facilities | invoices
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [history, setHistory] = useState([]);

  // Modals & form state
  const [modal, setModal] = useState(null); // 'agreement' | 'provider_discount' | 'refund' | 'switch_confirm' | 'pricing_edit' | 'invoice_view'
  const [modalData, setModalData] = useState({});
  const [modalForm, setModalForm] = useState({});
  const [busy, setBusy] = useState(false);

  // Filters for physician ledger
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerPlan, setLedgerPlan] = useState('');
  const [ledgerStatus, setLedgerStatus] = useState('');
  const [ledgerFacility, setLedgerFacility] = useState('');

  // Fetch initial data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (DEMO) {
        // Mock demo data for local simulation
        setMetrics({
          mrr_cents: 68600,
          arr_cents: 823200,
          active_professional_subscriptions: 2,
          active_facility_subscriptions: 3,
          total_billable_providers: 9,
          total_invoices_count: 5,
          revenue_collected_cents: 205800,
          pending_revenue_cents: 0,
          past_due_invoices_count: 0,
          total_refunds_cents: 0,
          stripe_live_mode: false,
          subscription_system_enabled: true,
          subscription_enforcement_enabled: true,
        });
        setSettings({
          subscription_system_enabled: true,
          plan_professional_enabled: true,
          plan_facility_enabled: true,
          automatic_payments_enabled: true,
          free_trials_enabled: true,
          ai_usage_billing_enabled: false,
          promotional_discounts_enabled: true,
          new_subscriptions_enabled: true,
          billing_notifications_enabled: true,
          subscription_enforcement_enabled: true,
          professional_monthly_cents: 10900,
          facility_base_monthly_cents: 14900,
          facility_per_provider_monthly_cents: 5900,
          annual_billing_enabled: true,
          annual_discount_percent: 15,
          trial_duration_days: 14,
          grace_period_days: 14,
          ai_monthly_included_notes: 100,
          ai_overage_rate_cents: 15,
          stripe_live_mode: false,
          stripe_currency: 'usd',
        });
        setPlans([
          {
            id: 'professional',
            name: 'Professional Plan',
            description: 'Designed for independent physicians, clinical psychologists, and solo practitioners.',
            default_price_cents: 10900,
            billing_interval: 'monthly',
            features: [
              'Complete Patient Charts & Electronic Health Records',
              'AI Clinical Note Copilot (Progress, SOAP & Consultations)',
              'Treatment Plans & Behavioral Goal Tracking',
              'ICD-10 & DSM-5 Diagnostic Search Engine',
              'Integrated Appointment & Office Scheduling',
            ],
          },
          {
            id: 'facility_group',
            name: 'Facility / Group Plan',
            description: 'Designed for healthcare facilities, PHP/PSR therapy centers, and multi-provider groups.',
            base_fee_cents: 14900,
            per_provider_fee_cents: 5900,
            billing_interval: 'monthly',
            features: [
              'Multi-Provider & Clinical Staff Management',
              'Facility Administration, Locations & Granular Roles',
              'PHP and PSR Therapy Programs documentation',
              'Group psychotherapy & individual therapy notes',
              'Standardized AI Clinical Note Copilot for all providers',
              'Facility-level analytics and compliance reporting',
              'Consolidated billing with per-provider tracking',
            ],
          },
        ]);
        setLedger([
          {
            id: 1,
            user_id: 101,
            physician_name: 'Dr. Jane Shepherd, MD',
            physician_email: 'jshepherd@metrohealth.org',
            physician_title: 'Medical Director',
            organization_name: 'Metro Health PHP Center',
            facility_code: 'MHC-01',
            facility_id: 1,
            plan_type: 'facility_group',
            rate_cents: 5900,
            total_cents: 5900,
            billing_owner: 'facility',
            invoice_number: 'INV-2026-0101',
            payment_status: 'paid',
            billing_period_start: '2026-10-01',
            billing_period_end: '2026-10-31',
            next_billing_date: '2026-11-01',
          },
          {
            id: 2,
            user_id: 102,
            physician_name: 'Dr. Robert Carter, MD',
            physician_email: 'rcarter@metrohealth.org',
            physician_title: 'Staff Psychiatrist',
            organization_name: 'Metro Health PHP Center',
            facility_code: 'MHC-01',
            facility_id: 1,
            plan_type: 'facility_group',
            rate_cents: 5900,
            total_cents: 5900,
            billing_owner: 'facility',
            invoice_number: 'INV-2026-0101',
            payment_status: 'paid',
            billing_period_start: '2026-10-01',
            billing_period_end: '2026-10-31',
            next_billing_date: '2026-11-01',
          },
          {
            id: 3,
            user_id: 105,
            physician_name: 'Dr. Sarah Lin, MD',
            physician_email: 'slin@privatepractice.org',
            physician_title: 'Neurologist',
            organization_name: 'Solo Neurology Practice',
            facility_code: null,
            facility_id: null,
            plan_type: 'professional',
            rate_cents: 10900,
            total_cents: 10900,
            billing_owner: 'physician',
            invoice_number: 'INV-2026-0089',
            payment_status: 'paid',
            billing_period_start: '2026-10-01',
            billing_period_end: '2026-10-31',
            next_billing_date: '2026-11-01',
          },
        ]);
        setFacilities([
          {
            facility_id: 1,
            facility_name: 'Metro Health PHP Center',
            facility_code: 'MHC-01',
            subscription_status: 'active',
            base_subscription_cents: 14900,
            billable_providers_count: 2,
            per_provider_cents: 5900,
            discount_percent: 0,
            discount_cents: 0,
            monthly_total_cents: 26700,
            next_renewal_date: '2026-11-01',
            custom_agreement_active: false,
          },
        ]);
        setInvoices([
          {
            id: 1,
            invoice_number: 'INV-2026-0101',
            facility_name: 'Metro Health PHP Center',
            total_cents: 26700,
            status: 'paid',
            created_at: '2026-10-01T00:00:00Z',
            paid_at: '2026-10-01T00:00:00Z',
            line_items: [
              { description: 'Facility/Group Monthly Base Fee', amount_cents: 14900 },
              { description: 'Clinical Provider Licenses (2 seats @ $59/mo)', amount_cents: 11800 },
            ],
          },
        ]);
        setHistory([
          {
            id: 1,
            created_at: '2026-10-01T12:00:00Z',
            actor_name: 'System Initializer',
            change_type: 'setting_update',
            setting_key: 'subscription_system_enabled',
            previous_value: 'false',
            new_value: 'true',
            reason: 'Production subscription management initial rollout',
          },
        ]);
      } else {
        const [metRes, setRes, ledRes, facRes, invRes, hisRes] = await Promise.all([
          api('/admin/billing/metrics'),
          api('/admin/billing/settings'),
          api('/admin/billing/ledger'),
          api('/admin/billing/facilities'),
          api('/admin/billing/invoices'),
          api('/admin/billing/history'),
        ]);

        setMetrics(metRes);
        setSettings(setRes.settings);
        setPlans(setRes.plans || []);
        setLedger(ledRes.ledger || []);
        setFacilities(facRes.facilities || []);
        setInvoices(invRes.invoices || []);
        setHistory(hisRes.history || []);
      }
    } catch (err) {
      notify(err.message || 'Failed to load billing configuration.');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Master switch toggling
  const toggleSwitch = async (key, currentValue, label) => {
    const newValue = !currentValue;
    // For critical switches (disabling the entire system or changing enforcement), prompt confirmation
    if (key === 'subscription_system_enabled' || key === 'subscription_enforcement_enabled') {
      setModal('switch_confirm');
      setModalData({ key, label, newValue });
      return;
    }
    await executeSwitchUpdate(key, newValue, `Toggled ${label} to ${newValue ? 'ON' : 'OFF'}`);
  };

  const executeSwitchUpdate = async (key, value, reason) => {
    setBusy(true);
    try {
      if (DEMO) {
        setSettings(prev => ({ ...prev, [key]: value }));
        setHistory(prev => [
          {
            id: Date.now(),
            created_at: new Date().toISOString(),
            actor_name: user?.name || 'Administrator',
            change_type: 'setting_update',
            setting_key: key,
            previous_value: String(!value),
            new_value: String(value),
            reason,
          },
          ...prev,
        ]);
        notify(`Updated ${key.replace(/_/g, ' ')} successfully.`);
      } else {
        const res = await api('/admin/billing/settings', {
          method: 'PATCH',
          body: { [key]: value, reason },
        });
        setSettings(res.settings);
        notify(`Updated ${key.replace(/_/g, ' ')} successfully.`);
        // Refresh metrics & history
        const [mRes, hRes] = await Promise.all([
          api('/admin/billing/metrics'),
          api('/admin/billing/history'),
        ]);
        setMetrics(mRes);
        setHistory(hRes.history || []);
      }
      setModal(null);
    } catch (err) {
      notify(err.message || 'Failed to update setting.');
    } finally {
      setBusy(false);
    }
  };

  // Pricing form save
  const handlePricingSave = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        professional_monthly_cents: Math.round(Number(modalForm.professional_monthly_dollars || 109) * 100),
        facility_base_monthly_cents: Math.round(Number(modalForm.facility_base_monthly_dollars || 149) * 100),
        facility_per_provider_monthly_cents: Math.round(Number(modalForm.facility_per_provider_monthly_dollars || 59) * 100),
        annual_discount_percent: Math.max(0, Math.min(100, Number(modalForm.annual_discount_percent || 15))),
        trial_duration_days: Math.max(0, Number(modalForm.trial_duration_days || 14)),
        grace_period_days: Math.max(0, Number(modalForm.grace_period_days || 14)),
        ai_monthly_included_notes: Math.max(0, Number(modalForm.ai_monthly_included_notes || 100)),
        ai_overage_rate_cents: Math.round(Number(modalForm.ai_overage_rate_dollars || 0.15) * 100),
        reason: modalForm.reason || 'Platform Administrator updated base pricing structure',
      };

      if (DEMO) {
        setSettings(prev => ({
          ...prev,
          professional_monthly_cents: payload.professional_monthly_cents,
          facility_base_monthly_cents: payload.facility_base_monthly_cents,
          facility_per_provider_monthly_cents: payload.facility_per_provider_monthly_cents,
          annual_discount_percent: payload.annual_discount_percent,
          trial_duration_days: payload.trial_duration_days,
          grace_period_days: payload.grace_period_days,
          ai_monthly_included_notes: payload.ai_monthly_included_notes,
          ai_overage_rate_cents: payload.ai_overage_rate_cents,
        }));
        notify('Pricing updated successfully (Demo mode).');
      } else {
        const res = await api('/admin/billing/settings', {
          method: 'PATCH',
          body: payload,
        });
        setSettings(res.settings);
        notify('Subscription pricing successfully updated and logged to audit trail.');
        const [mRes, hRes] = await Promise.all([
          api('/admin/billing/metrics'),
          api('/admin/billing/history'),
        ]);
        setMetrics(mRes);
        setHistory(hRes.history || []);
      }
      setModal(null);
    } catch (err) {
      notify(err.message || 'Failed to update pricing.');
    } finally {
      setBusy(false);
    }
  };

  // Facility custom agreement save
  const handleSaveFacilityAgreement = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const fid = modalData.facility_id;
      const payload = {
        custom_base_price_cents: modalForm.custom_base_price_dollars !== '' ? Math.round(Number(modalForm.custom_base_price_dollars) * 100) : null,
        custom_per_provider_cents: modalForm.custom_per_provider_dollars !== '' ? Math.round(Number(modalForm.custom_per_provider_dollars) * 100) : null,
        discount_percent: Number(modalForm.discount_percent || 0),
        agreement_notes: modalForm.agreement_notes || '',
      };

      if (DEMO) {
        setFacilities(prev =>
          prev.map(f => (f.facility_id === fid ? { ...f, custom_agreement_active: true, agreement_notes: payload.agreement_notes } : f))
        );
        notify('Custom facility agreement saved.');
      } else {
        await api(`/admin/billing/facilities/${fid}/agreement`, {
          method: 'PATCH',
          body: payload,
        });
        notify('Facility custom pricing agreement saved.');
        const facRes = await api('/admin/billing/facilities');
        setFacilities(facRes.facilities || []);
      }
      setModal(null);
    } catch (err) {
      notify(err.message || 'Failed to save agreement.');
    } finally {
      setBusy(false);
    }
  };

  // Physician discount & billing owner save
  const handleSavePhysicianDiscount = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const uid = modalData.user_id;
      const payload = {
        custom_rate_cents: modalForm.custom_rate_dollars !== '' ? Math.round(Number(modalForm.custom_rate_dollars) * 100) : null,
        discount_percent: Number(modalForm.discount_percent || 0),
        billing_owner: modalForm.billing_owner || 'facility',
        notes: modalForm.notes || '',
      };

      if (DEMO) {
        setLedger(prev =>
          prev.map(row => (row.user_id === uid ? { ...row, billing_owner: payload.billing_owner } : row))
        );
        notify('Provider pricing rules updated.');
      } else {
        await api(`/admin/billing/physicians/${uid}/discount`, {
          method: 'PATCH',
          body: payload,
        });
        notify('Physician billing discount and owner attribution saved.');
        const ledRes = await api('/admin/billing/ledger');
        setLedger(ledRes.ledger || []);
      }
      setModal(null);
    } catch (err) {
      notify(err.message || 'Failed to save provider discount.');
    } finally {
      setBusy(false);
    }
  };

  // Process Invoice Refund
  const handleProcessRefund = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const invId = modalData.id;
      const payload = {
        amount_cents: Math.round(Number(modalForm.amount_dollars || (modalData.total_cents / 100)) * 100),
        reason: modalForm.reason || 'Platform Administrator approved refund',
      };

      if (DEMO) {
        setInvoices(prev =>
          prev.map(inv => (inv.id === invId ? { ...inv, status: 'refunded', refund_amount_cents: payload.amount_cents } : inv))
        );
        notify('Refund processed (demo simulation).');
      } else {
        await api(`/admin/billing/invoices/${invId}/refund`, {
          method: 'POST',
          body: payload,
        });
        notify('Refund completed and recorded in billing ledger.');
        const [invRes, mRes] = await Promise.all([
          api('/admin/billing/invoices'),
          api('/admin/billing/metrics'),
        ]);
        setInvoices(invRes.invoices || []);
        setMetrics(mRes);
      }
      setModal(null);
    } catch (err) {
      notify(err.message || 'Failed to process refund.');
    } finally {
      setBusy(false);
    }
  };

  // Filtered ledger rows
  const filteredLedger = ledger.filter(item => {
    if (ledgerSearch) {
      const s = ledgerSearch.toLowerCase();
      const match =
        (item.physician_name && item.physician_name.toLowerCase().includes(s)) ||
        (item.physician_email && item.physician_email.toLowerCase().includes(s)) ||
        (item.organization_name && item.organization_name.toLowerCase().includes(s)) ||
        (item.invoice_number && item.invoice_number.toLowerCase().includes(s));
      if (!match) return false;
    }
    if (ledgerPlan && item.plan_type !== ledgerPlan) return false;
    if (ledgerStatus && item.payment_status !== ledgerStatus) return false;
    if (ledgerFacility && String(item.facility_id) !== String(ledgerFacility)) return false;
    return true;
  });

  // Export CSV
  const exportLedgerCsv = () => {
    const headers = [
      'Physician Name',
      'Email',
      'Title',
      'Facility Name',
      'Facility Code',
      'Plan Type',
      'Base Rate ($)',
      'Total ($)',
      'Billing Owner',
      'Invoice #',
      'Status',
      'Period Start',
      'Period End',
      'Next Billing Date',
    ];

    const rows = filteredLedger.map(r => [
      `"${r.physician_name || ''}"`,
      `"${r.physician_email || ''}"`,
      `"${r.physician_title || ''}"`,
      `"${r.organization_name || 'Independent'}"`,
      `"${r.facility_code || ''}"`,
      `"${r.plan_type === 'professional' ? 'Professional' : 'Facility/Group'}"`,
      (r.rate_cents / 100).toFixed(2),
      (r.total_cents / 100).toFixed(2),
      `"${r.billing_owner === 'facility' ? 'Facility' : 'Physician'}"`,
      `"${r.invoice_number || ''}"`,
      `"${r.payment_status || ''}"`,
      `"${r.billing_period_start || ''}"`,
      `"${r.billing_period_end || ''}"`,
      `"${r.next_billing_date || ''}"`,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `php_physician_billing_ledger_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && !settings) {
    return (
      <div className="content-card generation-skeleton" role="status">
        <i />
        <i />
        <i />
        <span>Loading subscription billing management…</span>
      </div>
    );
  }

  const s = settings || {};

  return (
    <div className="billing-management-wrapper" style={{ marginTop: '12px' }}>
      {/* HIPAA & Clinical Continuity Safeguard Banner */}
      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #cbd5e1',
          borderLeft: '4px solid #0284c7',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}
      >
        <ShieldCheck size={20} color="#0284c7" style={{ marginTop: '2px', flexShrink: 0 }} />
        <div>
          <strong style={{ color: '#0f172a', fontSize: '14px' }}>
            HIPAA Clinical Data & Medical Records Isolation Safeguard
          </strong>
          <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
            Physicians Health Portal enforces strict decoupling between subscription payment status and clinical records.
            <strong> Disabling subscriptions, non-payment, or plan modifications will NEVER delete clinical notes, patient charts, treatment plans, medications, or audit logs.</strong> Accounts in grace period maintain continuity-of-care read access.
          </p>
        </div>
      </div>

      {/* Sub-navigation tabs */}
      <div className="patient-tabs" style={{ marginBottom: '16px' }}>
        <button className={subTab === 'overview' ? 'selected' : ''} onClick={() => setSubTab('overview')}>
          Overview & Metrics
        </button>
        <button className={subTab === 'controls' ? 'selected' : ''} onClick={() => setSubTab('controls')}>
          Master Controls & Pricing
        </button>
        <button className={subTab === 'ledger' ? 'selected' : ''} onClick={() => setSubTab('ledger')}>
          Physician Billing Ledger
        </button>
        <button className={subTab === 'facilities' ? 'selected' : ''} onClick={() => setSubTab('facilities')}>
          Facility Agreements
        </button>
        <button className={subTab === 'invoices' ? 'selected' : ''} onClick={() => setSubTab('invoices')}>
          Invoices & Receipts
        </button>
      </div>

      {/* =========================================================================
          SUB-TAB 1: OVERVIEW & METRICS
         ========================================================================= */}
      {subTab === 'overview' && (
        <>
          {/* Key Metric Cards */}
          <div className="dashboard-grid" style={{ marginBottom: '20px' }}>
            <section className="content-card dash-card">
              <h3>Monthly Recurring Revenue</h3>
              <p className="big-number" style={{ color: '#0f766e' }}>{centsToDollars(metrics?.mrr_cents)}</p>
              <small style={{ color: '#64748b' }}>Annual Run Rate: {centsToDollars(metrics?.arr_cents)}</small>
            </section>
            <section className="content-card dash-card">
              <h3>Active Subscriptions</h3>
              <p className="big-number" style={{ color: '#1e293b' }}>
                {(metrics?.active_professional_subscriptions || 0) + (metrics?.active_facility_subscriptions || 0)}
              </p>
              <small style={{ color: '#64748b' }}>
                {metrics?.active_professional_subscriptions || 0} Professional · {metrics?.active_facility_subscriptions || 0} Facility/Group
              </small>
            </section>
            <section className="content-card dash-card">
              <h3>Billable Clinical Providers</h3>
              <p className="big-number" style={{ color: '#2563eb' }}>{metrics?.total_billable_providers || 0}</p>
              <small style={{ color: '#64748b' }}>Licensed Physicians, APRNs & Therapists</small>
            </section>
            <section className="content-card dash-card">
              <h3>Revenue Collected</h3>
              <p className="big-number" style={{ color: '#059669' }}>{centsToDollars(metrics?.revenue_collected_cents)}</p>
              <small style={{ color: '#64748b' }}>
                {metrics?.total_invoices_count || 0} invoices · {centsToDollars(metrics?.pending_revenue_cents)} pending
              </small>
            </section>
          </div>

          {/* Supported Plans Breakdown (Strictly Two Plans) */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', color: '#0f172a' }}>Standard Supported Subscription Plans</h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Physicians Health Portal operates exclusively on two straightforward subscription models.
                </p>
              </div>
              <button
                className="button primary"
                onClick={() => {
                  setModal('pricing_edit');
                  setModalForm({
                    professional_monthly_dollars: (s.professional_monthly_cents / 100).toFixed(2),
                    facility_base_monthly_dollars: (s.facility_base_monthly_cents / 100).toFixed(2),
                    facility_per_provider_monthly_dollars: (s.facility_per_provider_monthly_cents / 100).toFixed(2),
                    annual_discount_percent: s.annual_discount_percent,
                    trial_duration_days: s.trial_duration_days,
                    grace_period_days: s.grace_period_days,
                    ai_monthly_included_notes: s.ai_monthly_included_notes,
                    ai_overage_rate_dollars: (s.ai_overage_rate_cents / 100).toFixed(2),
                    reason: '',
                  });
                }}
              >
                <Edit3 size={15} /> Adjust Plan Pricing
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
              {/* PLAN 1: PROFESSIONAL */}
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <span className="badge purple" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <Users size={12} /> PLAN 1 · INDIVIDUAL PROVIDERS
                  </span>
                  <span className={`badge ${s.plan_professional_enabled ? 'green' : 'neutral'}`}>
                    {s.plan_professional_enabled ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
                <h3 style={{ margin: '8px 0 4px', fontSize: '20px', color: '#0f172a' }}>Professional Plan</h3>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', margin: '14px 0 16px' }}>
                  <span style={{ fontSize: '32px', fontWeight: '700', color: '#0f172a' }}>
                    {centsToDollars(s.professional_monthly_cents)}
                  </span>
                  <span style={{ color: '#64748b', fontSize: '14px' }}>/ month per provider</span>
                </div>
                <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, marginBottom: '16px' }}>
                  Designed for solo physicians, medical specialists, psychologists, and independent healthcare practitioners.
                </p>
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                  <h4 style={{ fontSize: '13px', textTransform: 'uppercase', color: '#64748b', marginBottom: '10px', letterSpacing: '0.5px' }}>
                    Included Clinical Capabilities:
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: '#334155' }}>
                    {[
                      'Complete Patient Charts & Electronic Health Records',
                      'Standardized AI Clinical Note Copilot (SOAP, Progress & Consultations)',
                      'Treatment Plans & Behavioral Goal Tracking',
                      'ICD-10 & DSM-5 Diagnostic Search Engine',
                      'Appointment Management & Office Scheduling',
                      'Prescription & Medication Documentation',
                      'HIPAA Security & Audit Logging',
                    ].map((item, idx) => (
                      <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <CheckCircle2 size={16} color="#059669" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* PLAN 2: FACILITY / GROUP */}
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <span className="badge blue" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <Building2 size={12} /> PLAN 2 · CLINICS & CENTERS
                  </span>
                  <span className={`badge ${s.plan_facility_enabled ? 'green' : 'neutral'}`}>
                    {s.plan_facility_enabled ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
                <h3 style={{ margin: '8px 0 4px', fontSize: '20px', color: '#0f172a' }}>Facility / Group Plan</h3>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', margin: '14px 0 16px' }}>
                  <span style={{ fontSize: '32px', fontWeight: '700', color: '#0f172a' }}>
                    {centsToDollars(s.facility_base_monthly_cents)}
                  </span>
                  <span style={{ color: '#64748b', fontSize: '14px' }}>
                    base + <strong style={{ color: '#0f172a' }}>{centsToDollars(s.facility_per_provider_monthly_cents)}</strong> / provider / mo
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, marginBottom: '16px' }}>
                  Designed for healthcare facilities, PHP/PSR therapy centers, behavioral hospitals, and multi-physician practices.
                </p>
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                  <h4 style={{ fontSize: '13px', textTransform: 'uppercase', color: '#64748b', marginBottom: '10px', letterSpacing: '0.5px' }}>
                    Included Facility Capabilities:
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: '#334155' }}>
                    {[
                      'Unlimited Physician & Clinical Staff Accounts',
                      'Facility Administration, Multiple Locations & Granular Roles',
                      'Full PHP & PSR Therapy Program Management',
                      'Individual & Group Psychotherapy Notes',
                      'Standardized AI Clinical Note Copilot across all providers',
                      'Facility Compliance & Attendance Reporting',
                      'Consolidated Monthly Billing with Provider Ledger',
                    ].map((item, idx) => (
                      <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <CheckCircle2 size={16} color="#2563eb" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Quick System Status Highlights */}
          <section className="content-card">
            <div className="content-heading">
              <div>
                <h3>Platform Billing Infrastructure Status</h3>
                <p>Live status of payment gateways, automated enforcement, and notifications.</p>
              </div>
            </div>
            <div className="demographics-grid">
              <div>
                <label>Global Subscription System</label>
                <p>
                  <span className={`badge ${s.subscription_system_enabled ? 'green' : 'neutral'}`}>
                    {s.subscription_system_enabled ? 'ACTIVE & ENFORCED' : 'GLOBALLY DISABLED (FREE PASS)'}
                  </span>
                </p>
              </div>
              <div>
                <label>Stripe Processing Mode</label>
                <p>
                  <span className="badge amber" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Lock size={12} /> STRIPE TEST / SIMULATION MODE
                  </span>
                </p>
              </div>
              <div>
                <label>Free Trial Offering</label>
                <p>{s.free_trials_enabled ? `${s.trial_duration_days} Days Free Trial Enabled` : 'Disabled'}</p>
              </div>
              <div>
                <label>Non-Payment Grace Period</label>
                <p>{s.grace_period_days} Days (Continuity-of-care preserved)</p>
              </div>
              <div>
                <label>Annual Pre-Payment Incentive</label>
                <p>{s.annual_billing_enabled ? `${s.annual_discount_percent}% Annual Discount` : 'Disabled'}</p>
              </div>
              <div>
                <label>AI Note Copilot Overage Billing</label>
                <p>{s.ai_usage_billing_enabled ? `Active (${centsToDollars(s.ai_overage_rate_cents)}/note over ${s.ai_monthly_included_notes})` : 'Included Free (Overage Off)'}</p>
              </div>
            </div>
          </section>
        </>
      )}

      {/* =========================================================================
          SUB-TAB 2: MASTER CONTROLS & PRICING
         ========================================================================= */}
      {subTab === 'controls' && (
        <>
          {/* Master 10 Toggles */}
          <section className="content-card" style={{ marginBottom: '24px' }}>
            <div className="content-heading">
              <div>
                <h3>Master Administrator Subscription Controls</h3>
                <p>
                  Instant platform-wide switches. Changes take effect immediately without recompilation or redeployment.
                </p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '16px' }}>
              {[
                {
                  key: 'subscription_system_enabled',
                  label: 'Enable Subscription System',
                  desc: 'Globally turn subscription requirement on/off. When OFF, all authorized clinical users access permitted features without subscription gating.',
                  val: s.subscription_system_enabled,
                  badge: s.subscription_system_enabled ? 'green' : 'red',
                },
                {
                  key: 'plan_professional_enabled',
                  label: 'Enable Professional Plan',
                  desc: 'Allow or prevent new Professional plan subscriptions ($109/month per provider).',
                  val: s.plan_professional_enabled,
                  badge: s.plan_professional_enabled ? 'green' : 'neutral',
                },
                {
                  key: 'plan_facility_enabled',
                  label: 'Enable Facility/Group Plan',
                  desc: 'Allow or prevent new Facility/Group subscriptions ($149/mo base + $59/mo per provider).',
                  val: s.plan_facility_enabled,
                  badge: s.plan_facility_enabled ? 'green' : 'neutral',
                },
                {
                  key: 'automatic_payments_enabled',
                  label: 'Enable Automatic Payments',
                  desc: 'Turn automatic recurring credit card / ACH charges on or off across all accounts.',
                  val: s.automatic_payments_enabled,
                  badge: s.automatic_payments_enabled ? 'green' : 'neutral',
                },
                {
                  key: 'free_trials_enabled',
                  label: 'Enable Free Trials',
                  desc: `Offer ${s.trial_duration_days}-day complimentary trials to newly registered physicians and facilities.`,
                  val: s.free_trials_enabled,
                  badge: s.free_trials_enabled ? 'green' : 'neutral',
                },
                {
                  key: 'ai_usage_billing_enabled',
                  label: 'Enable AI Usage Billing',
                  desc: 'Turn separate overage charges for the AI Clinical Note Copilot on or off. When OFF, usage is included at no additional cost.',
                  val: s.ai_usage_billing_enabled,
                  badge: s.ai_usage_billing_enabled ? 'green' : 'neutral',
                },
                {
                  key: 'promotional_discounts_enabled',
                  label: 'Enable Promotional Discounts',
                  desc: 'Allow or disable promotional coupons and custom discount codes during checkout.',
                  val: s.promotional_discounts_enabled,
                  badge: s.promotional_discounts_enabled ? 'green' : 'neutral',
                },
                {
                  key: 'new_subscriptions_enabled',
                  label: 'Enable New Subscriptions',
                  desc: 'Allow or pause new paid signups while preserving all currently active subscriber accounts.',
                  val: s.new_subscriptions_enabled,
                  badge: s.new_subscriptions_enabled ? 'green' : 'neutral',
                },
                {
                  key: 'billing_notifications_enabled',
                  label: 'Enable Billing Notifications',
                  desc: 'Send or silence automated customer invoices, upcoming renewal reminders, and payment receipts.',
                  val: s.billing_notifications_enabled,
                  badge: s.billing_notifications_enabled ? 'green' : 'neutral',
                },
                {
                  key: 'subscription_enforcement_enabled',
                  label: 'Enable Subscription Enforcement',
                  desc: 'If turned OFF, accounts with expired subscriptions or past-due balances retain access without clinical disruption.',
                  val: s.subscription_enforcement_enabled,
                  badge: s.subscription_enforcement_enabled ? 'green' : 'amber',
                },
              ].map(item => (
                <div
                  key={item.key}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <strong style={{ fontSize: '14px', color: '#0f172a' }}>{item.label}</strong>
                      <span className={`badge ${item.badge}`}>{item.val ? 'ON' : 'OFF'}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: 1.45 }}>{item.desc}</p>
                  </div>
                  <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      className={`button ${item.val ? '' : 'primary'}`}
                      style={{ fontSize: '12px', padding: '4px 12px' }}
                      disabled={busy}
                      onClick={() => toggleSwitch(item.key, item.val, item.label)}
                    >
                      {item.val ? 'Turn OFF' : 'Turn ON'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Base Pricing Structure Details */}
          <section className="content-card" style={{ marginBottom: '24px' }}>
            <div className="content-heading">
              <div>
                <h3>Standard Plan Pricing & Rules</h3>
                <p>Default charges applied to newly created subscriptions and standard renewal cycles.</p>
              </div>
              <button
                className="button primary"
                onClick={() => {
                  setModal('pricing_edit');
                  setModalForm({
                    professional_monthly_dollars: (s.professional_monthly_cents / 100).toFixed(2),
                    facility_base_monthly_dollars: (s.facility_base_monthly_cents / 100).toFixed(2),
                    facility_per_provider_monthly_dollars: (s.facility_per_provider_monthly_cents / 100).toFixed(2),
                    annual_discount_percent: s.annual_discount_percent,
                    trial_duration_days: s.trial_duration_days,
                    grace_period_days: s.grace_period_days,
                    ai_monthly_included_notes: s.ai_monthly_included_notes,
                    ai_overage_rate_dollars: (s.ai_overage_rate_cents / 100).toFixed(2),
                    reason: '',
                  });
                }}
              >
                <Edit3 size={15} /> Edit Pricing Parameters
              </button>
            </div>

            <div className="demographics-grid">
              <div>
                <label>Professional Plan Price</label>
                <p><strong>{centsToDollars(s.professional_monthly_cents)}</strong> / provider / month</p>
              </div>
              <div>
                <label>Facility Base Monthly Fee</label>
                <p><strong>{centsToDollars(s.facility_base_monthly_cents)}</strong> / facility / month</p>
              </div>
              <div>
                <label>Facility Per-Provider Seat Fee</label>
                <p><strong>{centsToDollars(s.facility_per_provider_monthly_cents)}</strong> / active provider / month</p>
              </div>
              <div>
                <label>Annual Pre-Payment Discount</label>
                <p>{s.annual_discount_percent}% off regular monthly rate</p>
              </div>
              <div>
                <label>Trial Period Duration</label>
                <p>{s.trial_duration_days} days full access</p>
              </div>
              <div>
                <label>Non-Payment Grace Period</label>
                <p>{s.grace_period_days} days continuity-of-care buffer</p>
              </div>
              <div>
                <label>Included AI Notes / Month</label>
                <p>{s.ai_monthly_included_notes} notes per seat</p>
              </div>
              <div>
                <label>AI Note Overage Rate</label>
                <p>{centsToDollars(s.ai_overage_rate_cents)} per additional generated note</p>
              </div>
            </div>
          </section>

          {/* Configuration Audit Trail */}
          <section className="content-card">
            <div className="content-heading">
              <div>
                <h3>Billing Configuration Audit History</h3>
                <p>Append-only audit trail tracking every modification to subscription toggles and rates.</p>
              </div>
            </div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Administrator</th>
                    <th>Setting</th>
                    <th>Previous Value</th>
                    <th>New Value</th>
                    <th>Reason / Justification</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(row => (
                    <tr key={row.id}>
                      <td>{formatDateTime(row.created_at)}</td>
                      <td><strong>{row.actor_name || 'System'}</strong></td>
                      <td><code>{row.setting_key}</code></td>
                      <td><span className="badge neutral">{String(row.previous_value)}</span></td>
                      <td><span className="badge green">{String(row.new_value)}</span></td>
                      <td>{row.reason || '—'}</td>
                    </tr>
                  ))}
                  {!history.length && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', color: '#94a3b8' }}>
                        No billing configuration changes logged yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {/* =========================================================================
          SUB-TAB 3: PHYSICIAN BILLING LEDGER
         ========================================================================= */}
      {subTab === 'ledger' && (
        <section className="content-card">
          <div className="content-heading">
            <div>
              <h3>Physician-Level Billing Ledger</h3>
              <p>
                Line-by-line attribution of provider subscription charges, discounts, and billing ownership.
              </p>
            </div>
            <button className="button" onClick={exportLedgerCsv}>
              <Download size={15} /> Export Ledger CSV
            </button>
          </div>

          {/* Clear Notice on Double-Charging Prevention */}
          <div
            style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '6px',
              padding: '10px 14px',
              fontSize: '12px',
              color: '#166534',
              marginBottom: '16px',
            }}
          >
            <strong>Double-Charging Safeguard Active:</strong> Clinical providers affiliated with a healthcare facility are billed through the facility's consolidated invoice (<strong>Billing Owner: Facility</strong>). Independent physicians with solo practices pay directly (<strong>Billing Owner: Physician</strong>). Neither group is double-billed.
          </div>

          {/* Filter Bar */}
          <div className="form-grid admin-form" style={{ marginBottom: '16px' }}>
            <label>
              Search Provider or Invoice
              <input
                value={ledgerSearch}
                onChange={e => setLedgerSearch(e.target.value)}
                placeholder="Physician name, email, facility or invoice #"
              />
            </label>
            <label>
              Plan Type
              <select value={ledgerPlan} onChange={e => setLedgerPlan(e.target.value)}>
                <option value="">All Plans</option>
                <option value="professional">Professional ($109/mo)</option>
                <option value="facility_group">Facility / Group ($59/seat)</option>
              </select>
            </label>
            <label>
              Payment Status
              <select value={ledgerStatus} onChange={e => setLedgerStatus(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="past_due">Past Due</option>
                <option value="refunded">Refunded</option>
              </select>
            </label>
          </div>

          {/* Ledger Table */}
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Physician</th>
                  <th>Facility Affiliation</th>
                  <th>Plan</th>
                  <th>Monthly Rate</th>
                  <th>Billing Owner</th>
                  <th>Invoice #</th>
                  <th>Status</th>
                  <th>Next Billing</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLedger.map(row => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.physician_name}</strong>
                      <br />
                      <small style={{ color: '#64748b' }}>{row.physician_email}</small>
                      {row.physician_title && (
                        <span className="badge neutral" style={{ marginLeft: '6px', fontSize: '10px' }}>
                          {row.physician_title}
                        </span>
                      )}
                    </td>
                    <td>
                      {row.organization_name ? (
                        <>
                          <span>{row.organization_name}</span>
                          {row.facility_code && <small style={{ color: '#64748b' }}> · {row.facility_code}</small>}
                        </>
                      ) : (
                        <span style={{ color: '#64748b', fontStyle: 'italic' }}>Independent Practice</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${row.plan_type === 'professional' ? 'purple' : 'blue'}`}>
                        {row.plan_type === 'professional' ? 'Professional' : 'Facility/Group'}
                      </span>
                    </td>
                    <td>
                      <strong>{centsToDollars(row.total_cents || row.rate_cents)}</strong>
                      {row.ai_charges_cents > 0 && (
                        <small style={{ color: '#0f766e', display: 'block' }}>
                          +{centsToDollars(row.ai_charges_cents)} AI overage
                        </small>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${row.billing_owner === 'facility' ? 'neutral' : 'amber'}`}>
                        {row.billing_owner === 'facility' ? 'Facility Invoiced' : 'Direct Physician'}
                      </span>
                    </td>
                    <td>
                      <code>{row.invoice_number || 'PENDING'}</code>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          row.payment_status === 'paid'
                            ? 'green'
                            : row.payment_status === 'past_due'
                            ? 'red'
                            : row.payment_status === 'refunded'
                            ? 'purple'
                            : 'amber'
                        }`}
                      >
                        {row.payment_status?.toUpperCase() || 'PAID'}
                      </span>
                    </td>
                    <td>{formatDate(row.next_billing_date)}</td>
                    <td>
                      <button
                        className="button"
                        style={{ fontSize: '11px', padding: '3px 8px' }}
                        onClick={() => {
                          setModal('provider_discount');
                          setModalData(row);
                          setModalForm({
                            custom_rate_dollars: (row.rate_cents / 100).toFixed(2),
                            discount_percent: row.discount_percent || 0,
                            billing_owner: row.billing_owner || 'facility',
                            notes: row.notes || '',
                          });
                        }}
                      >
                        <Edit3 size={12} /> Edit Rate
                      </button>
                    </td>
                  </tr>
                ))}
                {!filteredLedger.length && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', color: '#94a3b8' }}>
                      No ledger records found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* =========================================================================
          SUB-TAB 4: FACILITY AGREEMENTS & BILLING
         ========================================================================= */}
      {subTab === 'facilities' && (
        <section className="content-card">
          <div className="content-heading">
            <div>
              <h3>Facility Billing & Custom Pricing Agreements</h3>
              <p>
                Manage organization-level subscriptions, provider seat allocations, and custom pricing overrides.
              </p>
            </div>
          </div>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Facility Name</th>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Base Fee</th>
                  <th>Billable Seats</th>
                  <th>Seat Price</th>
                  <th>Discount</th>
                  <th>Monthly Total</th>
                  <th>Renewal Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {facilities.map(f => (
                  <tr key={f.facility_id}>
                    <td>
                      <strong>{f.facility_name}</strong>
                      {f.custom_agreement_active && (
                        <span className="badge purple" style={{ marginLeft: '6px', fontSize: '10px' }}>
                          Custom Agreement
                        </span>
                      )}
                    </td>
                    <td><code>{f.facility_code || '—'}</code></td>
                    <td>
                      <span className={`badge ${f.subscription_status === 'active' ? 'green' : 'amber'}`}>
                        {f.subscription_status?.toUpperCase()}
                      </span>
                    </td>
                    <td>{centsToDollars(f.base_subscription_cents)}</td>
                    <td>
                      <strong>{f.billable_providers_count}</strong> active providers
                    </td>
                    <td>{centsToDollars(f.per_provider_cents)} / seat</td>
                    <td>
                      {f.discount_percent > 0 ? (
                        <span className="badge green">{f.discount_percent}% OFF</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <strong style={{ color: '#0f766e', fontSize: '15px' }}>
                        {centsToDollars(f.monthly_total_cents)}
                      </strong>
                    </td>
                    <td>{formatDate(f.next_renewal_date)}</td>
                    <td>
                      <button
                        className="button"
                        style={{ fontSize: '11px', padding: '3px 8px' }}
                        onClick={() => {
                          setModal('agreement');
                          setModalData(f);
                          setModalForm({
                            custom_base_price_dollars: (f.base_subscription_cents / 100).toFixed(2),
                            custom_per_provider_dollars: (f.per_provider_cents / 100).toFixed(2),
                            discount_percent: f.discount_percent || 0,
                            agreement_notes: f.agreement_notes || '',
                          });
                        }}
                      >
                        <Edit3 size={12} /> Custom Agreement
                      </button>
                    </td>
                  </tr>
                ))}
                {!facilities.length && (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', color: '#94a3b8' }}>
                      No active facilities found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* =========================================================================
          SUB-TAB 5: INVOICES & RECEIPTS
         ========================================================================= */}
      {subTab === 'invoices' && (
        <section className="content-card">
          <div className="content-heading">
            <div>
              <h3>Subscription Invoices & Receipts</h3>
              <p>Complete historical record of issued invoices, line items, and refund processing.</p>
            </div>
          </div>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Invoice Number</th>
                  <th>Customer / Account</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Issued Date</th>
                  <th>Paid Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id}>
                    <td>
                      <code>{inv.invoice_number}</code>
                    </td>
                    <td>
                      <strong>{inv.facility_name || inv.subscriber_name || 'Independent Provider'}</strong>
                    </td>
                    <td>
                      <strong>{centsToDollars(inv.total_cents)}</strong>
                      {inv.refund_amount_cents > 0 && (
                        <small style={{ color: '#dc2626', display: 'block' }}>
                          Refunded {centsToDollars(inv.refund_amount_cents)}
                        </small>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          inv.status === 'paid'
                            ? 'green'
                            : inv.status === 'refunded'
                            ? 'purple'
                            : inv.status === 'past_due'
                            ? 'red'
                            : 'amber'
                        }`}
                      >
                        {inv.status?.toUpperCase()}
                      </span>
                    </td>
                    <td>{formatDate(inv.created_at)}</td>
                    <td>{formatDate(inv.paid_at)}</td>
                    <td>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button
                          className="button"
                          style={{ fontSize: '11px', padding: '3px 8px' }}
                          onClick={() => {
                            setModal('invoice_view');
                            setModalData(inv);
                          }}
                        >
                          <Receipt size={12} /> View Details
                        </button>
                        {inv.status === 'paid' && (
                          <button
                            className="button"
                            style={{ fontSize: '11px', padding: '3px 8px', color: '#dc2626' }}
                            onClick={() => {
                              setModal('refund');
                              setModalData(inv);
                              setModalForm({
                                amount_dollars: (inv.total_cents / 100).toFixed(2),
                                reason: '',
                              });
                            }}
                          >
                            Refund
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!invoices.length && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', color: '#94a3b8' }}>
                      No invoices issued yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* =========================================================================
          MODALS
         ========================================================================= */}

      {/* Modal: Edit Master Pricing Parameters */}
      {modal === 'pricing_edit' && (
        <Modal title="Adjust Platform Subscription Pricing" onClose={() => setModal(null)} wide>
          <form onSubmit={handlePricingSave}>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
              Update default pricing for the two supported plans. Changing these defaults applies to all future billing cycles and new signups. Facility-specific pricing agreements will be honored.
            </p>

            <div className="form-grid admin-form" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
              <label>
                Professional Plan Monthly Price ($)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={modalForm.professional_monthly_dollars || ''}
                  onChange={e => setModalForm({ ...modalForm, professional_monthly_dollars: e.target.value })}
                />
                <small style={{ color: '#64748b' }}>Default: $109.00 / physician / month</small>
              </label>

              <label>
                Facility / Group Base Fee ($)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={modalForm.facility_base_monthly_dollars || ''}
                  onChange={e => setModalForm({ ...modalForm, facility_base_monthly_dollars: e.target.value })}
                />
                <small style={{ color: '#64748b' }}>Default: $149.00 / facility / month</small>
              </label>

              <label>
                Facility Per-Provider Seat Fee ($)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={modalForm.facility_per_provider_monthly_dollars || ''}
                  onChange={e => setModalForm({ ...modalForm, facility_per_provider_monthly_dollars: e.target.value })}
                />
                <small style={{ color: '#64748b' }}>Default: $59.00 / provider / month</small>
              </label>

              <label>
                Annual Pre-Payment Discount (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  required
                  value={modalForm.annual_discount_percent || ''}
                  onChange={e => setModalForm({ ...modalForm, annual_discount_percent: e.target.value })}
                />
                <small style={{ color: '#64748b' }}>Default: 15%</small>
              </label>

              <label>
                Free Trial Duration (Days)
                <input
                  type="number"
                  min="0"
                  required
                  value={modalForm.trial_duration_days || ''}
                  onChange={e => setModalForm({ ...modalForm, trial_duration_days: e.target.value })}
                />
                <small style={{ color: '#64748b' }}>Default: 14 days</small>
              </label>

              <label>
                Continuity Grace Period (Days)
                <input
                  type="number"
                  min="0"
                  required
                  value={modalForm.grace_period_days || ''}
                  onChange={e => setModalForm({ ...modalForm, grace_period_days: e.target.value })}
                />
                <small style={{ color: '#64748b' }}>Default: 14 days</small>
              </label>

              <label>
                Included Monthly AI Notes (per seat)
                <input
                  type="number"
                  min="0"
                  required
                  value={modalForm.ai_monthly_included_notes || ''}
                  onChange={e => setModalForm({ ...modalForm, ai_monthly_included_notes: e.target.value })}
                />
                <small style={{ color: '#64748b' }}>Default: 100 notes / provider</small>
              </label>

              <label>
                AI Note Overage Rate ($)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={modalForm.ai_overage_rate_dollars || ''}
                  onChange={e => setModalForm({ ...modalForm, ai_overage_rate_dollars: e.target.value })}
                />
                <small style={{ color: '#64748b' }}>Default: $0.15 / note</small>
              </label>
            </div>

            <div style={{ marginTop: '14px' }}>
              <label>
                Audit Trail Justification / Reason
                <input
                  type="text"
                  required
                  placeholder="e.g. Approved 2026 Q4 annual pricing alignment"
                  value={modalForm.reason || ''}
                  onChange={e => setModalForm({ ...modalForm, reason: e.target.value })}
                />
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" className="button" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button type="submit" className="button primary" disabled={busy}>
                {busy ? 'Saving Changes…' : 'Save & Log Pricing Changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal: Custom Facility Agreement */}
      {modal === 'agreement' && (
        <Modal title={`Custom Agreement: ${modalData.facility_name}`} onClose={() => setModal(null)}>
          <form onSubmit={handleSaveFacilityAgreement}>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
              Override default platform subscription rates for this specific healthcare facility.
            </p>

            <div className="form-grid admin-form">
              <label>
                Custom Facility Base Fee ($)
                <input
                  type="number"
                  step="0.01"
                  placeholder="Leave blank for standard ($149)"
                  value={modalForm.custom_base_price_dollars || ''}
                  onChange={e => setModalForm({ ...modalForm, custom_base_price_dollars: e.target.value })}
                />
              </label>

              <label>
                Custom Per-Provider Seat Fee ($)
                <input
                  type="number"
                  step="0.01"
                  placeholder="Leave blank for standard ($59)"
                  value={modalForm.custom_per_provider_dollars || ''}
                  onChange={e => setModalForm({ ...modalForm, custom_per_provider_dollars: e.target.value })}
                />
              </label>

              <label>
                Special Agreement Discount (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={modalForm.discount_percent || ''}
                  onChange={e => setModalForm({ ...modalForm, discount_percent: e.target.value })}
                />
              </label>

              <label>
                Contractual Notes / Terms
                <textarea
                  rows="3"
                  placeholder="e.g., Annual enterprise contract signed Oct 2026"
                  value={modalForm.agreement_notes || ''}
                  onChange={e => setModalForm({ ...modalForm, agreement_notes: e.target.value })}
                />
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" className="button" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button type="submit" className="button primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save Agreement'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal: Edit Physician Pricing & Billing Owner */}
      {modal === 'provider_discount' && (
        <Modal title={`Provider Billing: ${modalData.physician_name}`} onClose={() => setModal(null)}>
          <form onSubmit={handleSavePhysicianDiscount}>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
              Set custom provider rates or alter billing ownership between direct physician and facility consolidated billing.
            </p>

            <div className="form-grid admin-form">
              <label>
                Billing Owner
                <select
                  value={modalForm.billing_owner || 'facility'}
                  onChange={e => setModalForm({ ...modalForm, billing_owner: e.target.value })}
                >
                  <option value="facility">Facility (Consolidated Facility Invoice)</option>
                  <option value="physician">Physician (Direct Individual Invoice)</option>
                </select>
                <small style={{ color: '#64748b' }}>
                  Determines whether this clinician is included on the facility's bill or billed separately.
                </small>
              </label>

              <label>
                Custom Provider Monthly Rate ($)
                <input
                  type="number"
                  step="0.01"
                  placeholder="Standard rate"
                  value={modalForm.custom_rate_dollars || ''}
                  onChange={e => setModalForm({ ...modalForm, custom_rate_dollars: e.target.value })}
                />
              </label>

              <label>
                Provider Discount (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={modalForm.discount_percent || ''}
                  onChange={e => setModalForm({ ...modalForm, discount_percent: e.target.value })}
                />
              </label>

              <label>
                Notes / Justification
                <input
                  type="text"
                  placeholder="e.g. Courtesy staff waiver or academic discount"
                  value={modalForm.notes || ''}
                  onChange={e => setModalForm({ ...modalForm, notes: e.target.value })}
                />
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" className="button" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button type="submit" className="button primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save Provider Settings'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal: Process Invoice Refund */}
      {modal === 'refund' && (
        <Modal title={`Issue Refund for ${modalData.invoice_number}`} onClose={() => setModal(null)}>
          <form onSubmit={handleProcessRefund}>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
              Process a full or partial refund for this invoice. The refund will be attributed in the physician billing ledger and logged in the audit trail.
            </p>

            <div className="form-grid admin-form">
              <label>
                Refund Amount ($)
                <input
                  type="number"
                  step="0.01"
                  required
                  max={(modalData.total_cents / 100).toFixed(2)}
                  value={modalForm.amount_dollars || ''}
                  onChange={e => setModalForm({ ...modalForm, amount_dollars: e.target.value })}
                />
                <small style={{ color: '#64748b' }}>Maximum refundable: {centsToDollars(modalData.total_cents)}</small>
              </label>

              <label>
                Refund Reason / Justification
                <input
                  type="text"
                  required
                  placeholder="e.g. Facility billing seat adjustment"
                  value={modalForm.reason || ''}
                  onChange={e => setModalForm({ ...modalForm, reason: e.target.value })}
                />
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" className="button" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button type="submit" className="button primary" style={{ background: '#dc2626' }} disabled={busy}>
                {busy ? 'Processing Refund…' : 'Confirm & Issue Refund'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal: Invoice View Details */}
      {modal === 'invoice_view' && (
        <Modal title={`Invoice Details: ${modalData.invoice_number}`} onClose={() => setModal(null)}>
          <div style={{ padding: '8px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <strong style={{ fontSize: '16px' }}>{modalData.facility_name || modalData.subscriber_name}</strong>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>Issued on {formatDate(modalData.created_at)}</p>
              </div>
              <span className={`badge ${modalData.status === 'paid' ? 'green' : 'amber'}`}>
                {modalData.status?.toUpperCase()}
              </span>
            </div>

            <h4 style={{ fontSize: '13px', textTransform: 'uppercase', color: '#64748b', marginBottom: '8px' }}>
              Line Items
            </h4>
            <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
              {(modalData.line_items || []).map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                    borderBottom: idx < modalData.line_items.length - 1 ? '1px solid #e2e8f0' : 'none',
                    fontSize: '13px',
                  }}
                >
                  <span>{item.description}</span>
                  <strong>{centsToDollars(item.amount_cents)}</strong>
                </div>
              ))}
              {!modalData.line_items?.length && (
                <div style={{ fontSize: '13px', color: '#64748b' }}>Standard Monthly Subscription Fee</div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', padding: '8px 0' }}>
              <span>Total Invoice Amount:</span>
              <span style={{ color: '#0f766e' }}>{centsToDollars(modalData.total_cents)}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" className="button" onClick={() => setModal(null)}>
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Confirmation for Critical Switches */}
      {modal === 'switch_confirm' && (
        <Modal title={`Confirm Master Switch: ${modalData.label}`} onClose={() => setModal(null)}>
          <div style={{ padding: '8px 0' }}>
            <p style={{ fontSize: '14px', color: '#1e293b', lineHeight: 1.5 }}>
              Are you sure you want to turn <strong>{modalData.newValue ? 'ON' : 'OFF'}</strong> the setting{' '}
              <strong>"{modalData.label}"</strong>?
            </p>
            {modalData.key === 'subscription_system_enabled' && !modalData.newValue && (
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#991b1b',
                  fontSize: '12px',
                  marginTop: '10px',
                }}
              >
                Turning OFF the global subscription system allows all clinical accounts and facilities to access PHP clinical features without subscription gating.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
              <button type="button" className="button" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="button primary"
                disabled={busy}
                onClick={() =>
                  executeSwitchUpdate(
                    modalData.key,
                    modalData.newValue,
                    `Platform Administrator confirmed master toggle to ${modalData.newValue ? 'ON' : 'OFF'}`
                  )
                }
              >
                {busy ? 'Updating…' : 'Confirm & Apply'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
